/**
 * Module: RecommendationsList
 * Role: Fetches GET /api/v2/buyer/tasks and renders a 2-col grid of offer cards.
 *       queryKey matches TasksScreen (['buyer','tasks']) for shared cache (DA-8).
 *       Loading → 4 Skeleton placeholders. Error/empty → honest empty state.
 *       Card tap → navigate('/product/' + offer_id) (in-app, react-router).
 *       Prototype match: marketplace badge (overlay, top-left), discount badge
 *       (−XX%, green pill, top-right when price known), Caveat "+X ₽ вернётся"
 *       handwriting accent below price.
 *
 * Inline interfaces — per WalletCard/TasksScreen precedent; never import from api/types.ts (L-240).
 *
 * Uses:
 *   - @tanstack/react-query: useQuery
 *   - react-router-dom: useNavigate
 *   - api/client: apiFetch
 *   - components/atoms: Card, ProductImg, Tag, Skeleton, EmptyState, Ic
 *   - styles/tokens: P, S, T
 *   - lib/format: formatKopecks
 *
 * Used by:
 *   - screens/HomeScreen/index
 *
 * Glossary: ai/glossary/buyer.md
 */
import { useInfiniteQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { fetchTasksPage, getNextTasksPageParam, type TaskItem, type TasksResponse } from '../../api/tasks';
import { Card, ProductImg, Skeleton, EmptyState, Ic } from '../../components/atoms';
import { P, S, T } from '../../styles/tokens';
import { formatKopecks } from '../../lib/format';

// Types imported from ../../api/tasks (BUG-1164 — SSOT)

// ─── Sub-component ───────────────────────────────────────────────────────────
interface RecommendationCardProps {
  item: TaskItem;
  onTap: (offerId: string) => void;
}

function RecommendationCard({ item, onTap }: RecommendationCardProps) {
  // Discount % only shown when price_kopecks is known and > cashback_kopecks
  const discountPct =
    item.price_kopecks && item.price_kopecks > item.cashback_kopecks
      ? Math.round((item.cashback_kopecks / item.price_kopecks) * 100)
      : null;

  return (
    <Card
      p={0}
      r={16}
      hover
      onClick={() => onTap(item.offer_id)}
      style={{ overflow: 'hidden', cursor: 'pointer' }}
    >
      {/* Image section with overlay badges */}
      <div style={{ position: 'relative' }}>
        {item.product_image_url ? (
          <img
            src={item.product_image_url}
            alt={item.product_title}
            style={{
              width: '100%',
              height: 170,
              objectFit: 'cover',
              display: 'block',
            }}
          />
        ) : (
          <ProductImg
            h={170}
            w="100%"
            r={0}
            hue={item.product_title.charCodeAt(0) % 360}
          />
        )}
        {/* Marketplace badge — top-left, white semi-transparent pill */}
        <div style={{
          position: 'absolute',
          top: 8,
          left: 8,
          background: 'rgba(255,255,255,0.92)',
          color: S.label,
          padding: '3px 8px',
          borderRadius: 6,
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: 0.1,
          lineHeight: 1.3,
        }}>
          {item.marketplace}
        </div>
        {/* Discount badge — top-right, green pill, only when price known */}
        {discountPct !== null && (
          <div style={{
            position: 'absolute',
            top: 8,
            right: 8,
            background: 'rgba(45,138,78,0.88)',
            color: '#fff',
            padding: '3px 7px',
            borderRadius: 999,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: 0.1,
            lineHeight: 1.3,
          }}>
            −{discountPct}%
          </div>
        )}
      </div>

      {/* Content section */}
      <div style={{ padding: '10px 12px 12px' }}>
        {/* Product title — 2-line clamp */}
        <p
          style={{
            font: T.headline,
            color: S.label,
            margin: '0 0 6px',
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            minHeight: 44,
          } as React.CSSProperties}
        >
          {item.product_title}
        </p>

        {/* Price row — shown only when price_kopecks known */}
        {item.price_kopecks && item.price_kopecks > 0 && (
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 4 }}>
            <span style={{
              font: T.footnote,
              color: S.secondaryLabel,
              textDecoration: 'line-through',
            }}>
              {formatKopecks(item.price_kopecks)}
            </span>
          </div>
        )}

        {/* Cashback row — Caveat handwriting accent (prototype pattern) */}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, flexWrap: 'wrap' }}>
          <span style={{
            fontFamily: T.fontAccent,
            fontSize: 19,
            fontWeight: 700,
            color: P.terraDark,
            lineHeight: 1,
            whiteSpace: 'nowrap',
          }}>
            +{formatKopecks(item.cashback_kopecks)}
          </span>
          <span style={{ font: T.captionAlt, color: S.secondaryLabel }}>
            вернётся
          </span>
        </div>
      </div>
    </Card>
  );
}

// ─── Skeleton card ────────────────────────────────────────────────────────────
function SkeletonCard() {
  return (
    <div style={{
      borderRadius: 16,
      overflow: 'hidden',
      background: P.card,
      border: `1px solid ${P.line}`,
    }}>
      <Skeleton variant="block" h={170} r={0} />
      <div style={{ padding: '10px 12px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Skeleton variant="block" h={14} r={4} />
        <Skeleton variant="block" h={14} r={4} style={{ width: '60%' }} />
        <Skeleton variant="block" h={18} r={6} style={{ width: '50%', marginTop: 4 }} />
      </div>
    </div>
  );
}

// ─── RecommendationsList ──────────────────────────────────────────────────────
export function RecommendationsList() {
  const navigate = useNavigate();

  // CRITICAL: queryKey ['buyer','tasks'] matches TasksScreen — DA-8 cache sharing (BUG-1164)
  const { data, isPending, isError } = useInfiniteQuery<TasksResponse>({
    queryKey: ['buyer', 'tasks'],
    queryFn: ({ pageParam }) => fetchTasksPage(pageParam as number),
    initialPageParam: 0,
    getNextPageParam: getNextTasksPageParam,
    retry: false,
  });

  // Home widget renders ONLY first page (no fetchNextPage here).
  const firstPage = data?.pages[0];
  const count = firstPage?.items.length ?? 0;

  return (
    // SYMMETRIC LIMIT (ADR-022 §6) — `data-coach="feed"` mirrors COACH_TARGETS.feed in
    // screens/onboarding/coachmarks.ts (peers: WalletCard "wallet", PageHeader "bell").
    // Always-mounted outer div = FTR-1114 coachmarks tour anchor (highlightable even when
    // the list is empty/loading).
    <div data-coach="feed">
      {/* Section header — always rendered; serif T.title1 matches prototype */}
      <p style={{ font: T.title1, color: S.label, lineHeight: 1.1, margin: 0, letterSpacing: -0.3 }}>
        Находки сегодня
      </p>
      <p style={{ font: T.subhead, color: S.secondaryLabel, marginTop: 4, marginBottom: 0 }}>
        {isPending ? ' ' : `${count} товаров`}
      </p>

      {/* Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 12 }}>
        {/* Loading state — 4 skeleton placeholders matching card anatomy */}
        {isPending && (
          <>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </>
        )}

        {/* Error state — keep it distinct from a truly empty feed. */}
        {!isPending && isError && (
          <div style={{ gridColumn: '1 / -1', paddingTop: 16 }}>
            <EmptyState
              icon={<Ic name="tag" size={28} color={P.muted} />}
              title="Не удалось загрузить задания"
              subtitle="Проверь соединение и обнови экран"
            />
          </div>
        )}

        {/* Empty state — honest, polished, single message spanning both columns */}
        {!isPending && !isError && count === 0 && (
          <div style={{ gridColumn: '1 / -1', paddingTop: 16 }}>
            <EmptyState
              icon={<Ic name="tag" size={28} color={P.muted} />}
              title="Нет доступных заданий"
              subtitle="Новые находки появятся совсем скоро"
            />
          </div>
        )}

        {/* Populated grid — first page only (home widget) */}
        {!isPending && !isError && firstPage && count > 0 &&
          firstPage.items.map((item) => (
            <RecommendationCard
              key={item.offer_id}
              item={item}
              onTap={(offerId) => navigate('/product/' + offerId)}
            />
          ))
        }
      </div>
    </div>
  );
}
